<template>
  <div class="general-wrapper">
    <Header @toParent="showSavePopup" :parentText="$t('navigation.back')">
      <template #supertitle>
        <div class="gap-column-1xs editor-supertitle">
          <div
            class="
              qrw-heading-sm
              bold
              qrw-text-content-base-primary
              editor-supertitle-value
            "
          >
            {{ formTitleShorted || $t("checklist_editor.new_ph") }}
          </div>

          <BaseBadge
            variant="secondary"
            color="yellow-2"
            v-if="isEditedAfterPublish"
          >
            <div class="orange-4-color">
              {{ $t("checklist_editor.badge_edited") }}
            </div>
          </BaseBadge>
          <BaseBadge
            variant="primary"
            color="background-success-default"
            v-else-if="form.published_at"
          >
            <div class="qrw-text-content-positive">
              {{ $t("checklist_editor.badge_live") }}
            </div>
          </BaseBadge>
          <BaseBadge variant="secondary" color="overlay-8" v-else>
            {{ $t("checklist_editor.badge_draft") }}
          </BaseBadge>
        </div>
      </template>

      <template #actions>
        <div class="header-buttons">
          <BasePushButton
            @click="isDeleting = true"
            size="sm"
            variant="fill"
            bgColor="background-base-secondary"
            class="checklist-editor-header-button"
            textColor="content-error-primary"
          >
            {{ $t("checklist_editor.btn_delete") }}
          </BasePushButton>
          <BasePushButton
            @click="copyForm"
            size="sm"
            variant="fill"
            bgColor="background-base-secondary"
            class="checklist-editor-header-button"
            textColor="content-base-primary"
          >
            {{ $t("checklist_editor.btn_copy") }}
          </BasePushButton>
          <div class="iqos-tooltip-helper-box">
            <IQOSTooltip
              :earPosition="'top'"
              v-if="showingPreview"
              @close="showingPreview = false"
            >
              <img :src="formPreviewUrl" />
            </IQOSTooltip>
            <BasePushButton
              @click="showingPreview = true"
              size="sm"
              variant="fill"
              bgColor="background-base-secondary"
              class="checklist-editor-header-button"
              textColor="content-base-primary"
            >
              {{ $t("checklist_editor.btn_preview") }}
            </BasePushButton>
          </div>
          <BasePushButton
            size="sm"
            variant="fill"
            bgColor="background-warning-default"
            textColor="content-base-inverse-primary"
            class="checklist-editor-header-button"
            @click="publishTemplate"
            v-if="isEditedAfterPublish"
          >
            {{ $t("checklist_editor.btn_save") }}
          </BasePushButton>
          <BasePushButton
            size="sm"
            variant="fill"
            bgColor="background-success-default"
            textColor="content-base-inverse-primary"
            class="checklist-editor-header-button"
            @click="publishTemplate"
            v-if="!form.published_at && canPublishForm"
          >
            {{ $t("checklist_editor.btn_publish") }}
          </BasePushButton>
          <BasePushButton
            size="sm"
            variant="fill"
            bgColor="background-error-default"
            class="checklist-editor-header-button"
            textColor="content-base-inverse-primary"
            @click="unpublishTemplate"
            v-else-if="form.published_at"
          >
            {{ $t("checklist_editor.btn_unpublish") }}
          </BasePushButton>
        </div>
      </template>
    </Header>
    <ChecklistEditorTabs :tabs="editorTabs" v-model="currentTab" />

    <PageContainer
      :contentId="currentTab == 'content' ? 'checklist-editor-container' : null"
      style="position: relative"
      v-if="form._id"
    >
      <template v-if="currentTab == 'content'">
        <div class="checklist-form-content">
          <ChecklistBlockBase buttonsPreset="none" :hoverable="false">
            <BaseTextInput
              :placeholder="$t('checklist_editor.add_tag_ph')"
              v-model="newTagName"
              @keyup="onTagInputKeyup"
              size="sm"
            />
            <div class="gap-column-txs" v-if="form.tags.length">
              <BaseTag
                withIcon
                size="md"
                removable
                @remove="form.tags.splice(index, 1)"
                v-for="(tag, index) in form.tags"
                :key="tag"
              >
                {{ tag }}
              </BaseTag>
            </div>
          </ChecklistBlockBase>

          <ChecklistBlockBase buttonsPreset="none" :hoverable="false">
            <BaseTextarea
              autoresize
              class="form-title-input"
              :placeholder="$t('checklist_editor.title_ph')"
              v-model="form.title"
              :maxlength="128"
              @blur="saveForm"
              :rows="1"
              :resizable="false"
            />
            <BaseDivider color="border-secondary" />
            <BaseTextarea
              autoresize
              class="form-desc-input"
              :placeholder="$t('checklist_editor.desc_ph')"
              :value="form.description"
              @blur="saveForm"
              :maxlength="5000"
              :rows="1"
              :resizable="false"
            />
          </ChecklistBlockBase>

          <!-- TODO: use dynamic components -->
          <div
            :ref="`block${index}`"
            v-for="(block, index) in targetFormBlocks"
            :key="index"
          >
            <StaticTextBlock
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :block="block"
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-if="block.type === 'STATIC_TEXT'"
            />
            <DividerBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'DIVIDER'"
            />
            <MediaFileBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'MEDIA_FILE'"
            />
            <TextInputBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'TEXT_INPUT'"
            />
            <NumberInputBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'NUMBER'"
            />
            <ChecklistBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'CHECKLIST'"
            />
            <FileUploadBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'FILE_UPLOAD'"
            />
            <DocumentFileBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'DOCUMENT_FILE'"
            />
            <OptionsBlock
              :block="block"
              @commonAction="
                (action) => onBlockCommonAction(block, index, action)
              "
              :editorMetadata="getEditorMetadataForBlock(block, index)"
              v-else-if="block.type === 'OPTIONS'"
            />
          </div>
        </div>

        <ChecklistEditorToolbar @click="onToolbarButtonClicked" />
      </template>

      <template v-else-if="currentTab == 'schedule'">
        <div class="checklist-form-content gap-row-md">
          <div class="checklist-schedule-toggles gap-column-sm">
            <div
              class="checklist-schedule-toggle"
              @click="setScheduleType('adhoc')"
              :class="{ active: form.schedule_type === 'adhoc' }"
            >
              <BaseIcon icon="bookmark-filled" style="font-size: 48px" />
              <div class="qrw-heading-sm bold qrw-text-content-base-primary">
                {{ $t("checklist_editor.type_adhoc") }}
              </div>
              <div class="qrw-label-md qrw-text-content-base-secondary">
                {{ $t("checklist_editor.type_adhoc_desc") }}
              </div>
            </div>
            <div
              class="checklist-schedule-toggle"
              @click="setScheduleType('scheduled')"
              :class="{ active: form.schedule_type === 'scheduled' }"
            >
              <BaseIcon icon="calendar-filled" style="font-size: 48px" />
              <div class="qrw-heading-sm bold qrw-text-content-base-primary">
                {{ $t("checklist_editor.type_scheduled") }}
              </div>
              <div class="qrw-label-md qrw-text-content-base-secondary">
                {{ $t("checklist_editor.type_scheduled_desc") }}
              </div>
            </div>
          </div>
          <BaseDivider color="border-tertiary" />
          <div
            class="qrw-text-content-base-primary qrw-heading-md bold adhoc-text"
            v-if="form.schedule_type == 'adhoc'"
          >
            {{ $t("checklist_editor.type_adhoc_content") }}
          </div>
          <div class="gap-row-xs" v-else>
            <div class="gap-column-xs">
              <div class="qrw-heading-sm bold qrw-text-content-base-primary w-100">
                {{
                  $tc(
                    "checklist_editor.schedules_noun",
                    form.schedule_items.length
                  )
                }}
              </div>
              <BasePushButton
                style="min-width: fit-content"
                icon="plus"
                variant="fill"
                textColor="content-base-inverse-primary"
                size="sm"
                @click="addScheduleItem"
                bgColor="background-base-inverse-primary"
              >
                {{ $t("checklist_editor.btn_add_schedule") }}
              </BasePushButton>
            </div>

            <ChecklistScheduleItem
              :item="item"
              @update="saveForm"
              @delete="removeScheduleItem(index)"
              v-for="(item, index) in form.schedule_items"
              :key="index"
            />
          </div>
        </div>
      </template>

      <template v-else-if="currentTab == 'assign'">
        <div class="checklist-assign-content gap-row-xs">
          <div class="qrw-heading-sm bold qrw-text-content-base-primary">
            {{
              $tc("checklist_editor.members_noun", form.assigned_members.length)
            }}
          </div>
          <AssignedMemberRow
            v-for="(id, index) in form.assigned_members"
            :key="id"
            :waiterId="id"
            @delete="removeTeamMember(index)"
          />
          <BasePushButton
            @click="assigningTeamMember = true"
            variant="stroke"
            size="sm"
            icon="plus"
            textColor="content-base-secondary"
            hoverColor="content-base-inverse-primary"
            bgColor="background-base-inverse-primary"
            borderColor="border-secondary"
            class="assign-button"
          >
            {{ $t("checklist_editor.btn_add_member") }}
          </BasePushButton>
        </div>

        <WhiteModal
          useTopPortal
          hideFooter
          :title="$t('checklist_editor.btn_add_member')"
          v-if="assigningTeamMember"
          @close="assigningTeamMember = false"
        >
          <div class="gap-row-txs">
            <BaseSearchInput
              v-model="search"
              size="md"
              :placeholder="$t('checklist_editor.team_search_ph')"
            />
            <div
              @click="assignTeamMember(member)"
              class="assignable-member-item qrw-label-md"
              v-for="member in assignableTeamMembers"
              :key="member._id"
            >
              <div class="qrw-heading-xs bold qrw-text-content-base-primary w-100">
                {{ member.name }}
              </div>
              <div class="qrw-heading-xs qrw-text-content-base-secondary w-100">
                <!-- Manager -->
              </div>
              <div class="qrw-heading-xs qrw-text-secondary w-100">
                <!-- Department -->
              </div>
            </div>
          </div>
        </WhiteModal>
      </template>

      <portal-target name="white-modal-portal"> </portal-target>
    </PageContainer>

    <FormDeletePopup @close="isDeleting = false" @confirm="deleteForm" v-if="isDeleting "/>
    <FormSavePopup @close="isSaving = false" @confirm="s => returnToForms(s)" v-if="isSaving "/>
  </div>
</template>

<script>
import ChecklistEditorTabs from "@/components/checklists/ChecklistEditorTabs.vue";
import ChecklistEditorToolbar from "@/components/checklists/ChecklistEditorToolbar.vue";
import ChecklistBlockBase from "@/components/checklists/blocks/ChecklistBlockBase.vue";

import StaticTextBlock from "@/components/checklists/blocks/StaticTextBlock.vue";
import DividerBlock from "@/components/checklists/blocks/DividerBlock.vue";
import MediaFileBlock from "@/components/checklists/blocks/MediaFileBlock.vue";
import DocumentFileBlock from "@/components/checklists/blocks/DocumentFileBlock.vue";
import TextInputBlock from "@/components/checklists/blocks/TextInputBlock.vue";
import NumberInputBlock from "@/components/checklists/blocks/NumberInputBlock.vue";
import ChecklistBlock from "@/components/checklists/blocks/ChecklistBlock.vue";
import FileUploadBlock from "@/components/checklists/blocks/FileUploadBlock.vue";
import OptionsBlock from "@/components/checklists/blocks/OptionsBlock.vue";

import ChecklistScheduleItem from "@/components/checklists/ChecklistScheduleItem.vue";

import FormDeletePopup from "@/components/checklists/FormDeletePopup.vue"
import FormSavePopup from "@/components/checklists/FormSavePopup.vue"

import ChecklistsAPI from "@/api/checklists";


import AssignedMemberRow from "@/components/checklists/AssignedMemberRow.vue";

import "overlayscrollbars/styles/overlayscrollbars.min.css";
import { OverlayScrollbars } from "overlayscrollbars";

import moment from "moment";

let osInstance;

export default {
  data() {
    return {
      form: {
        title: "test",
        blocks: [],
      },
      currentTab: "content",
      assigningTeamMember: false,

      newTagName: null,

      autosaveTimerId: null,

      showingPreview: false,

      isDeleting: false,

      isSaving: false
    };
  },
  components: {
    ChecklistEditorTabs,
    ChecklistBlockBase,
    ChecklistEditorToolbar,

    AssignedMemberRow,

    StaticTextBlock,
    DividerBlock,
    MediaFileBlock,
    TextInputBlock,
    NumberInputBlock,
    ChecklistBlock,
    DocumentFileBlock,
    OptionsBlock,

    FileUploadBlock,
    ChecklistScheduleItem,

    FormDeletePopup,
    FormSavePopup
  },
  methods: {
    onToolbarButtonClicked(blockType) {
      const newBlockDraft = {
        type: blockType,
        title: null,
        required: true,
        cached: false,
        restricted: false,
      };

      if (blockType === "STATIC_TEXT")
        newBlockDraft.static_text = {
          content: "",
          style: "p",
        };

      if (blockType === "MEDIA_FILE")
        newBlockDraft.media_file = {
          url: null,
          type: null,
        };

      if (blockType === "NUMBER")
        newBlockDraft.number = {
          prefix: null,
          suffix: null,
          min_value: null,
          max_value: null,
        };
      if (blockType === "CHECKLIST")
        newBlockDraft.checklist = {
          rows: [
            {
              text: null,
            },
          ],
          comment_required: false,
          must_complete_all: true,
        };
      if (blockType === "DOCUMENT_FILE")
        newBlockDraft.document = {
          size: null,
          name: null,
          url: null,
        };
      if (blockType === "OPTIONS")
        newBlockDraft.options = {
          rows: [],
          display_mode: "dropdown",
        };

      this.targetFormBlocks.push(newBlockDraft);

      this.onBlockUpdated(newBlockDraft);

      this.$nextTick(() => {
        const viewport = osInstance.elements().viewport;

        viewport.scrollTop = viewport.scrollHeight;
      });
    },
    onBlockUpdated(block) {
      this.saveForm();
      this.$set(this.form, "blocks_updated_at", new Date());
    },
    onBlockCommonAction(block, index, action) {
      this.onBlockUpdated(block);

      if (action === "delete") {
        const newBlocks = this.targetFormBlocks.filter(
          (_, filterIndex) => index != filterIndex
        );

        const optionIds =
          block.type === "OPTIONS"
            ? block.options.rows.map((r) => r.option_id)
            : [];

        newBlocks.forEach((nb) => {
          if (nb.dependency_option_id) {
            if (optionIds.includes(nb.dependency_option_id)) {
              nb.dependency_option_id = null;
            }
          }
        });

        this.$set(this.form, "blocks", newBlocks);

        return;
      }

      if (action === "copy") {
        const newBlock = JSON.parse(JSON.stringify(block));

        this.targetFormBlocks.splice(index + 1, 0, newBlock);
        return;
      }

      if (action === "move-up" || action === "move-down") {
        let newIndex = action === "move-up" ? index - 1 : index + 1;

        if (newIndex < 0) return;
        if (newIndex > this.targetFormBlocks.length - 1) return;

        //swap the blocks
        const temp = this.targetFormBlocks[newIndex];
        this.$set(this.targetFormBlocks, index, temp);
        this.$set(this.targetFormBlocks, newIndex, block);

        //because there can be a lot of blocks, we try too keep the same block in center of viewport
        //by scrolling in the way of movement the container itself
        this.$nextTick(() => {
          const targetBlockEl = this.$refs[`block${index}`][0];

          const viewport = osInstance.elements().viewport;

          viewport.scrollTop +=
            targetBlockEl.offsetHeight * (action == "move-up" ? -1 : 1);
        });

        return;
      }
    },
    cancelChanges() {
      this.$router.push("/checklists/templates");
    },
    showSavePopup() {
      if (this.isEditedAfterPublish) {
        this.isSaving = true;
      } else {
        this.returnToForms(false);
      }
    },
    returnToForms(shouldSave) {
      if (!shouldSave) {
        this.$router.push("/checklists/templates");
        return;
      }

      ChecklistsAPI.saveTemplate(this.$route.params.id, this.form).then(
        (data) => {
          this.$router.push("/checklists/templates");
        }
      );
    },
    saveForm() {
      ChecklistsAPI.saveTemplate(this.$route.params.id, this.form).then(
        (data) => {
          this.form = data.template;
        }
      );
    },
    publishTemplate() {
      ChecklistsAPI.saveTemplate(this.$route.params.id, this.form).then(() => {
        ChecklistsAPI.publishTemplate(this.$route.params.id).then((data) => {
          this.form = data.template;
        });
      });
    },
    unpublishTemplate() {
      ChecklistsAPI.unpublishTemplate(this.$route.params.id).then((data) => {
        this.form = data.template;
      });
    },
    addScheduleItem() {
      this.form.schedule_items.push({
        is_repeated: false,
        base_date: new Date(),
        end_date: new Date(),
        start_date: new Date(),

        repeat_order: 1,
        repeat_period_unit: "day",
        repeat_periods: [],
      });
    },
    assignTeamMember(waiter) {
      if (!this.form.assigned_members) {
        this.$set(this.form, "assigned_members", []);
      }
      if (!this.form.assigned_members.includes(waiter._id)) {
        this.form.assigned_members.push(waiter._id);
      }
      this.assigningTeamMember = false;
      this.saveForm();
    },
    onTagInputKeyup(ev) {
      if (ev.keyCode === 13) {
        if (!this.form.tags.includes(this.newTagName))
          this.form.tags.push(this.newTagName);
        this.newTagName = null;
        this.saveForm();
      }
    },
    getEditorMetadataForBlock(block, index) {
      return {
        buttonsData: {
          firstBlock: index === 0,
          lastBlock: this.targetFormBlocks.length - 1 === index,
        },
        canAssignOptions:
          this.targetFormBlocks
            .slice(0, index)
            .filter((b) => b.type === "OPTIONS").length > 0,
        optionsBlocksAbove: this.targetFormBlocks
          .slice(0, index)
          .filter((b) => b.type === "OPTIONS"),
      };
    },
    copyForm() {
      ChecklistsAPI.copyTemplate(this.$route.params.id).then((data) => {
        this.$store.commit("ui/addToast", {
          variant: "confirm",
          content: this.$t("checklist_editor.copied"),
        });
        this.$router.replace(`/checklists/templates/${data.template._id}`);
        this.form = data.template;
      });
    },
    deleteForm() {
      this.isDeleting = false;

      ChecklistsAPI.deleteTemplate(this.$route.params.id).then((data) => {
        this.$router.push(`/checklists`);
      });
    },
    initScrollbar() {
      osInstance = OverlayScrollbars(
        document.querySelector("#checklist-editor-container"),
        {}
      );
    },
    removeTeamMember(index) {
      this.form.assigned_members.splice(index, 1);
      this.saveForm();
    },
    removeScheduleItem(index) {
      this.form.schedule_items.splice(index, 1);
      this.saveForm();
    },
    autosave() {
      if (this.form.published_at && !this.isEditedAfterPublish) return;

      if (
        this.form.updated_at &&
        moment(this.form.updated_at).isAfter(this.form.blocks_updated_at)
      )
        return;

      this.saveForm();
    },
    setScheduleType(type) {
      this.$set(this.form, 'schedule_type', type);
      this.$set(this.form, "blocks_updated_at", new Date());
    }
  },
  beforeDestroy() {
    clearInterval(this.autosaveTimerId);
  },
  computed: {
    editorTabs() {
      return [
        {
          key: "content",
          text: this.$t("checklist_editor.tab_content"),
        },
        {
          key: "schedule",
          text: this.$t("checklist_editor.tab_schedule"),
        },
        {
          key: "assign",
          text: this.$t("checklist_editor.tab_assign"),
        },
      ];
    },
    teamMembers() {
      return this.$store.state.waiters;
    },
    assignableTeamMembers() {
      return this.$store.state.waiters.filter(
        (w) => !this.form.assigned_members.includes(w._id)
      );
    },
    canPublishForm() {
      return this.form.title && this.targetFormBlocks.length;
    },
    isEditedAfterPublish() {
      return (
        this.form.published_at &&
        this.form.blocks_updated_at &&
        moment(this.form.published_at).isBefore(this.form.blocks_updated_at)
      );
    },
    targetFormBlocks() {
      return this.form.published_at ? this.form.draft_blocks : this.form.blocks;
    },
    formPreviewUrl() {
      const final_link = `https://pos.qrwaiter.com.ua/checklists/preview/${this.form._id}`;

      return `https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${final_link}`;
    },
    formTitleShorted() {
      if (this.form.title && this.form.title.length >= 26) {
        return this.form.title.substring(0,24) + '...';
      } else {
        return this.form.title;
      }
    }
  },
  mounted() {
    ChecklistsAPI.getTemplate(this.$route.params.id)
      .then((data) => {
        if (!data.template) return this.$router.push("/checklists");

        this.form = data.template;

        this.$store.commit("checklists/setForm", this.form);

        setTimeout(() => this.initScrollbar(), 1000);
        this.autosaveTimerId = setInterval(this.autosave, 30 * 1000);
      })
      .catch((err) => {
        console.error(err);
        this.$router.push("/checklists/templates");
      });
  },
};
</script>

<style scoped>
.checklist-form-content {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding-top: 20px;
  padding-bottom: 60px;
}

.checklist-assign-content {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding-top: 20px;
  padding-bottom: 40px;
}

.form-title-input {
  outline: none;
  border: none;
  background: transparent;
  height: 40px;
  -webkit-appearance: none;
  padding: 0 !important;
  width: 100%;
  font-size: 32px;
  color: var(--qrw-content-primary);
  font-weight: bold;
  resize: none !important;
  line-height: 40px;
}

.form-title-input::placeholder,
.form-desc-input::placeholder {
  color: var(--qrw-content-tertiary) !important;
}

.form-title-input:focus,
.form-desc-input:focus {
  border: 0 !important;
}

.form-desc-input {
  outline: none;
  border: none;
  background: transparent;
  height: 28px;
  -webkit-appearance: none;
  padding: 0 !important;
  width: 100%;
  font-size: 18px;
  color: var(--qrw-content-secondary);
  resize: none !important;
  line-height: 28px;
}

.header-buttons {
  display: flex;
  flex-direction: row;
  column-gap: 12px;
}

.checklist-schedule-toggle {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 40px;
  border-radius: 12px;
  cursor: pointer;
  background: var(--qrw-background-base-secondary);
  width: 100%;
  box-sizing: border-box;
  border: 4px solid transparent;
}

.checklist-schedule-toggle.active {
  background: var(--qrw-background-base-primary);
  border: 4px solid var(--qrw-border-primary);
  box-shadow: 0px 2px 4px 2px rgba(0, 0, 0, 0.16);
}

.adhoc-text {
  text-align: center;
}

.editor-supertitle {
  align-items: center;
}

.editor-supertitle-value {
  /* overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  white-space: nowrap; */
}

.assign-button {
  width: fit-content;
}

.assignable-member-item {
  display: flex;
  flex-direction: row;
  padding: 10px 16px;
  column-gap: 16px;
  border-radius: 8px;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
}

.assignable-member-item:hover {
  background: var(--qrw-background-base-secondary);
}

.checklist-editor-header-button {
  white-space: nowrap;
  min-width: fit-content;
}
</style>

<style>
#checklist-editor-container {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
